import { useEffect, useState } from 'react'
import type { MeDto } from '@praesto/shared'
import { api, ApiError } from '../api.js'
import { useRichText, useText } from '../i18n.js'
import { Brandmark } from '../components/Brandmark.js'
import { LegalFooter } from '../components/LegalFooter.js'
import { LanguageSwitch } from '../components/LanguageSwitch.js'

/**
 * Login, „Passwort vergessen" und – solange noch kein Konto existiert – das
 * Anlegen des ersten. Danach werden weitere Konten nur noch aus der
 * angemeldeten App heraus erzeugt.
 */
export function LoginPage({ onSignedIn }: { onSignedIn: (user: MeDto) => void }): React.ReactElement {
  const t = useText()
  const rich = useRichText()
  const [needsBootstrap, setNeedsBootstrap] = useState<boolean | null>(null)
  const [forgot, setForgot] = useState(false)
  const [sent, setSent] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [orgName, setOrgName] = useState('Digitalwart')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    api
      .authStatus()
      .then((status) => setNeedsBootstrap(status.needsBootstrap))
      .catch(() => setNeedsBootstrap(false))
  }, [])

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      if (forgot) {
        await api.forgotPassword(email)
        setSent(true)
        return
      }
      const user = needsBootstrap
        ? await api.register(
            email,
            password,
            displayName || email.split('@')[0]!,
            orgName.trim() || undefined,
          )
        : await api.login(email, password)
      onSignedIn(user)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('login.failed'))
    } finally {
      setBusy(false)
    }
  }

  /*
   * Nach dem Absenden steht hier bewusst keine Auskunft darüber, ob es die
   * Adresse gibt. Der Server antwortet aus demselben Grund immer gleich: Sonst
   * wäre dieses Formular die Auskunftsstelle, die der Login nicht sein soll.
   */
  if (sent) {
    return (
      <div className="auth-screen brand-surface">
        <div className="auth-card">
          <Brandmark />
          <p className="auth-lead">{rich('login.sent', { email: <strong>{email}</strong> })}</p>
          <button
            className="button ghost"
            onClick={() => {
              setSent(false)
              setForgot(false)
              setPassword('')
            }}
          >
            {t('login.backToSignIn')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="auth-screen brand-surface">
      <form className="auth-card" onSubmit={submit}>
        <Brandmark />
        <p className="auth-lead">
          {forgot
            ? t('login.lead.forgot')
            : needsBootstrap
              ? t('login.lead.bootstrap')
              : t('login.lead.default')}
        </p>

        {needsBootstrap && !forgot && (
          <label className="field">
            <span>{t('common.organisation')}</span>
            <input
              value={orgName}
              onChange={(event) => setOrgName(event.target.value)}
              placeholder={t('login.orgPlaceholder')}
            />
          </label>
        )}

        {needsBootstrap && !forgot && (
          <label className="field">
            <span>{t('common.name')}</span>
            <input
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              autoComplete="name"
              placeholder={t('login.namePlaceholder')}
            />
          </label>
        )}

        <label className="field">
          <span>{t('common.email')}</span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
          />
        </label>

        {!forgot && (
          <label className="field">
            <span>{t('common.password')}</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete={needsBootstrap ? 'new-password' : 'current-password'}
              required
              minLength={needsBootstrap ? 10 : undefined}
            />
            {needsBootstrap && <small>{t('account.minLength')}</small>}
          </label>
        )}

        {error && <p className="form-error">{error}</p>}

        <button className="button primary" type="submit" disabled={busy || needsBootstrap === null}>
          {busy
            ? t('common.moment')
            : forgot
              ? t('login.requestLink')
              : needsBootstrap
                ? t('login.createAccount')
                : t('login.signIn')}
        </button>

        {!needsBootstrap && (
          <>
            <button
              className="button link"
              type="button"
              onClick={() => {
                setForgot(!forgot)
                setError(null)
              }}
            >
              {forgot ? t('login.backToSignIn') : t('login.forgot')}
            </button>
            {/* Voller Seitenwechsel, kein Router-Link: Die Landingpage ist eine
                eigenständige Datei und gehört nicht zur App. */}
            <a className="button link" href="/de/">
              {t('login.whatIsPraesto')}
            </a>
          </>
        )}

        {/*
          Der Sprachumschalter steht schon hier und nicht erst im Konto: Wer
          diese Seite nicht liest, kommt nie an eine Einstellung dahinter.
        */}
        <LanguageSwitch />
      </form>

      {/* Auch hier: Der Anmeldebildschirm ist eine öffentlich erreichbare Seite
          und braucht die Anbieterkennzeichnung ebenso wie die Startseite. */}
      <LegalFooter />
    </div>
  )
}
